import { AlertTriangle, Construction, Inbox, RotateCw } from 'lucide-react';
import type { ReactNode } from 'react';
import { errorMessage, isApiError } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Skeleton } from './skeleton';

export function EmptyState({
  icon,
  title,
  description,
  action,
  className,
}: {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2 px-6 py-12 text-center',
        className,
      )}
    >
      <div className="grid size-10 place-content-center rounded-full bg-muted text-muted-foreground [&_svg]:size-5">
        {icon ?? <Inbox />}
      </div>
      <p className="font-medium">{title}</p>
      {description && <p className="max-w-sm text-ui text-muted-foreground">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/**
 * Error panel for failed queries. A 404 on a list endpoint almost always means the API
 * feature hasn't shipped yet, so say that instead of a scary error.
 */
export function ErrorState({
  error,
  onRetry,
  className,
}: {
  error: unknown;
  onRetry?: () => void;
  className?: string;
}) {
  const notReady = isApiError(error) && error.isNotFound;
  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-2 px-6 py-12 text-center',
        className,
      )}
    >
      <div
        className={cn(
          'grid size-10 place-content-center rounded-full [&_svg]:size-5',
          notReady ? 'bg-warning/15 text-warning' : 'bg-destructive/10 text-destructive',
        )}
      >
        {notReady ? <Construction /> : <AlertTriangle />}
      </div>
      <p className="font-medium">{notReady ? 'Not available yet' : 'Could not load data'}</p>
      <p className="max-w-md text-ui text-muted-foreground">
        {notReady
          ? 'The server does not provide this feature yet. It will appear here once the API is updated.'
          : errorMessage(error)}
      </p>
      {onRetry && (
        <Button variant="outline" size="sm" onClick={onRetry} className="mt-2">
          <RotateCw /> Retry
        </Button>
      )}
    </div>
  );
}

export function TableSkeleton({ rows = 8, cols = 5 }: { rows?: number; cols?: number }) {
  return (
    <div className="grid gap-2 p-3" aria-busy>
      {[...Array(rows).keys()].map((r) => (
        <div key={r} className="flex gap-3">
          {[...Array(cols).keys()].map((c) => (
            <Skeleton key={c} className={cn('h-5', c === 0 ? 'w-1/4' : 'flex-1')} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Renders loading / error / content for a query, so pages don't repeat the same ternaries. */
export function QueryState<T>({
  query,
  loading,
  children,
}: {
  query: { data: T | undefined; error: unknown; isPending: boolean; refetch: () => unknown };
  loading?: ReactNode;
  children: (data: T) => ReactNode;
}) {
  if (query.isPending) return loading ?? <TableSkeleton />;
  if (query.error || query.data === undefined)
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return children(query.data);
}
